import {
  formatMoney,
  formatRating,
} from '@components/layout/side-nav/bars/top-bar/metricFormat';
import type { TopBarDashboardResponse } from '@components/layout/side-nav/bars/top-bar/topBarTypes';
import { TOP_BAR_HEIGHT } from '@components/layout/side-nav/layout/layoutStyle';
import { MetricStat, Text } from '@components/ui';
import {
  countJobsByKind,
  jobKindBuckets,
  JobKindChip,
} from '@features/jobs';
import R8trJobs, {
  defaultR8trJobFiltersByTab,
  type R8trJobFiltersByTab,
  type R8trJobListTab,
} from '@features/r8tr/jobs/r8tr-jobs/r8tr-jobs';
import type { R8trJob } from '@features/r8tr/jobs/r8tr-jobs/r8trJobsTypes';
import GoogleMaps from '@features/r8tr/map/google-map/google-map';
import { NotificationsCard } from '@features/r8tr/notifications/notifications-card';
import { useDashboardQuery } from '@lib/api/dashboard';
import { useAvailableJobsQuery } from '@lib/api/jobs';
import { useProfileQuery } from '@lib/api/profile';
import {
  FEATURE_FLAG_KEYS,
  useFeatureFlag,
} from '@lib/featureFlags';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import StarIcon from '@mui/icons-material/Star';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { UserDetails } from './userTypes';

const statIconSx = { fontSize: 18, color: 'inherit' };

const SIDEBAR_WIDTH = 340;
const SIDEBAR_STICKY_TOP = TOP_BAR_HEIGHT + 16;
const SIDEBAR_STATS_HEIGHT = 168;
const SIDEBAR_KIND_HEIGHT = 56;
const NOTIFICATIONS_HEADER_HEIGHT = 64;

const R8trDashboard = (): JSX.Element => {
  const { t } = useTranslation();
  const navigator = useNavigate();
  const [showDashboard, setShowDashboard] = useState(false);
  const [jobTab, setJobTab] = useState<R8trJobListTab>('available');
  const [filtersByTab, setFiltersByTab] = useState<R8trJobFiltersByTab>(
    defaultR8trJobFiltersByTab
  );
  const { data } = useProfileQuery();
  const userDetails = data as UserDetails | undefined;
  const { data: dashboardBody = {} } = useDashboardQuery();
  const stats = (dashboardBody as TopBarDashboardResponse).data;
  const { data: availableJobs = [] } = useAvailableJobsQuery();
  const openJobs = availableJobs.length;
  const showCurrentJobs = jobTab === 'current';
  const nonAutomotiveEnabled = useFeatureFlag(
    FEATURE_FLAG_KEYS.NON_AUTOMOTIVE_CATEGORY
  );
  const kindCounts = countJobsByKind(availableJobs as R8trJob[]);
  const visibleKinds = jobKindBuckets.filter(
    (bucket) =>
      kindCounts[bucket] > 0 &&
      (nonAutomotiveEnabled || bucket !== 'NON_AUTOMOTIVE')
  );
  const sidebarListMaxHeight = `calc(100dvh - ${
    SIDEBAR_STICKY_TOP +
    SIDEBAR_STATS_HEIGHT +
    (visibleKinds.length > 0 ? SIDEBAR_KIND_HEIGHT : 0) +
    NOTIFICATIONS_HEADER_HEIGHT +
    24
  }px)`;

  useEffect(() => {
    if (localStorage.getItem('userRole') === 'r8tr') {
      const profile = userDetails?.data;
      const user = profile?.user;
      if (user?.phoneNumber && profile) {
        if (
          user.firstName == null ||
          user.firstName === '' ||
          user.email == null ||
          user.email === ''
        ) {
          navigator('/personal-info');
        } else if ((profile.areas?.length ?? 0) < 1) {
          navigator('/work-profile?step=areas');
        } else if ((profile.levelOfExpertise?.length ?? 0) < 1) {
          navigator('/work-profile?step=expertise');
        } else {
          setShowDashboard(true);
        }
      }
    } else {
      setShowDashboard(true);
    }
  }, [userDetails, navigator]);

  if (!showDashboard) {
    return <Box />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100%',
        maxWidth: 1120,
        mx: 'auto',
        px: { xs: 2, md: 4 },
        py: 3,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            lg: `minmax(0, 1fr) ${SIDEBAR_WIDTH}px`,
          },
          alignItems: 'start',
          gap: 3,
        }}
      >
        <Box
          sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}
        >
          <Box>
            <Text as="h2" variant="subtitle" sx={{ mb: 1.5 }}>
              {t('jobs.mapSection')}
            </Text>
            <Box
              sx={{
                height: { xs: 400, md: 560 },
                overflow: 'hidden',
                borderRadius: 3,
                border: 1,
                borderColor: 'divider',
              }}
            >
              <GoogleMaps
                showCurrentJobs={showCurrentJobs}
                jobFilter={filtersByTab[jobTab]}
              />
            </Box>
          </Box>
          <R8trJobs
            tab={jobTab}
            onTabChange={setJobTab}
            filters={filtersByTab}
            onFiltersChange={setFiltersByTab}
          />
        </Box>
        <Box component="aside" sx={{ alignSelf: { lg: 'stretch' } }}>
          <Box
            sx={{
              position: { lg: 'sticky' },
              top: { lg: SIDEBAR_STICKY_TOP },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                '& > *': { width: '100%', boxSizing: 'border-box' },
              }}
            >
              <MetricStat
                icon={<MonetizationOnIcon sx={statIconSx} />}
                label={t('shell.totalEarned')}
                value={formatMoney(stats?.totalEarned)}
              />
              <MetricStat
                icon={<StarIcon sx={statIconSx} />}
                label={t('shell.myRating')}
                value={formatRating(stats?.currentR8trRating)}
              />
              <MetricStat
                icon={<WorkOutlineIcon sx={statIconSx} />}
                label={t('shell.openJobs')}
                value={String(openJobs)}
              />
            </Box>
            {visibleKinds.length > 0 ? (
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}
                title={t('jobs.kindSectionHint')}
              >
                <Text variant="caption" tone="muted">
                  {t('jobs.kindSection')}
                </Text>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.75,
                  }}
                >
                  {visibleKinds.map((bucket) => (
                    <Box
                      key={bucket}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Text variant="label">{kindCounts[bucket]}</Text>
                      <JobKindChip bucket={bucket} />
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : null}
            <NotificationsCard
              listMaxHeight={{ xs: 320, lg: sidebarListMaxHeight }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default R8trDashboard;
